import React, {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
    buildWeeklyChart,
    formatLongDate,
    formatWeekTick,
    weekStartDates,
    weeksTotal,
} from './portalUsageState';

/**
 * Consultas del cliente por semana — 12 puntos, SVG a mano.
 *
 * Sin librería a propósito: el repo ya tomó esa decisión para trazados de este tamaño
 * (`agency/v2/Sparkline.js`), y acá pesa el doble porque el mismo lenguaje visual se dibuja
 * también en miniatura en cada fila del ranking cross-cliente.
 *
 * Tres reglas del diseño que este componente tiene que sostener (validadas en el mockup):
 *
 * 1. **El eje Y se escala al máximo del período.** Un cliente con 3 consultas semanales muestra su
 *    forma, no una línea aplastada contra el piso. Medido en producción, ese es el caso frecuente.
 * 2. **Cada punto lleva su número encima**, justamente porque esa escala es relativa: sin el
 *    número, 3 consultas y 300 dibujan exactamente la misma curva.
 * 3. **Serie entera en cero → un guion, no una línea plana.** Una línea pegada al piso se lee como
 *    un dato ("usó poquito") y el dato real es "no usó nada".
 *
 * La lectura del punto va en UNA línea arriba del gráfico y no en 12 tooltips: son 12 valores que
 * el usuario recorre con el mouse, no 12 objetos que necesiten su propia ficha.
 */
export function PortalWeeklyChart({weeks, now}) {
    const {t, i18n} = useTranslation();
    const [hovered, setHovered] = useState(null);

    const chart = useMemo(() => buildWeeklyChart(weeks), [weeks]);
    const weekStarts = useMemo(() => weekStartDates(chart.series.length, now || new Date()),
        [chart.series.length, now]);

    if (weeksTotal(chart.series) === 0) {
        return (
            <p className="pu-chart__empty">
                {t('portal_usage.chart_empty', 'Sin consultas en las últimas 12 semanas.')}
            </p>
        );
    }

    const {box, plot, points, levels} = chart;
    const hoveredPoint = hovered === null ? null : points[hovered];

    const readLine = hoveredPoint
        ? readLabel(t, formatLongDate(weekStarts[hoveredPoint.index], i18n.language), hoveredPoint.value)
        : t('portal_usage.chart_caption', 'Últimas 12 semanas.');

    return (
        <div className="pu-chart">
            {/* `aria-live` para que la lectura del punto llegue también a un lector de pantalla:
                el hover sobre un SVG no anuncia nada por sí solo. */}
            <p className="pu-chart__read" aria-live="polite">{readLine}</p>

            <svg
                className="pu-chart__svg"
                viewBox={`0 0 ${box.width} ${box.height}`}
                role="img"
                aria-label={t('portal_usage.chart_aria',
                    'Consultas del cliente por semana en las últimas 12 semanas')}
                onMouseLeave={() => setHovered(null)}
            >
                {levels.map(level => (
                    <g key={`lvl-${level.value}`}>
                        <line
                            className={`pu-chart__grid${level.value === 0 ? ' -base' : ''}`}
                            x1={plot.x0} y1={level.y.toFixed(1)} x2={plot.x1} y2={level.y.toFixed(1)}
                        />
                        <text className="pu-chart__ylbl" x={plot.x0 - 9} y={(level.y + 3.5).toFixed(1)}
                              textAnchor="end">{level.value}</text>
                    </g>
                ))}

                <path className="pu-chart__area" d={chart.areaPath}/>
                <path className="pu-chart__line" d={chart.linePath}/>

                {points.map(point => (
                    <g key={`pt-${point.index}`}>
                        <circle
                            className={`pu-chart__pt${point.value ? '' : ' -zero'}`}
                            cx={point.x.toFixed(1)} cy={point.y.toFixed(1)} r={point.value ? 4.6 : 2.4}
                        />
                        {/* Las semanas en cero no llevan número: un "0" doce veces es ruido, y el
                            punto gris chico ya dice que esa semana no pasó nada. */}
                        {point.value ? (
                            <text className="pu-chart__val" x={point.x.toFixed(1)}
                                  y={(point.y - 12).toFixed(1)} textAnchor="middle">{point.value}</text>
                        ) : null}
                        <text
                            className={`pu-chart__xlbl${point.value ? ' -on' : ''}`}
                            x={point.x.toFixed(1)} y={plot.y1 + 19} textAnchor="middle"
                        >{formatWeekTick(weekStarts[point.index])}</text>
                    </g>
                ))}

                {/* Una banda por semana para el hover: leer un punto no debería exigir apuntarle a
                    un círculo de 4px. */}
                {points.map(point => (
                    <rect
                        key={`hit-${point.index}`}
                        className="pu-chart__hit"
                        x={(point.x - (plot.dx / 2)).toFixed(1)}
                        y={plot.y0}
                        width={plot.dx.toFixed(1)}
                        height={plot.y1 - plot.y0}
                        onMouseEnter={() => setHovered(point.index)}
                    />
                ))}
            </svg>
        </div>
    );
}

function readLabel(t, week, value) {
    if (!value) {
        return t('portal_usage.chart_read_empty', 'Semana del {{week}} · sin actividad',
            {week, interpolation: {escapeValue: false}});
    }
    // Singular y plural como DOS keys y no con el `count` de i18next: el proyecto no declara
    // variantes `_one`/`_other`, así que un `count` caería siempre en el mismo texto.
    const key = value === 1 ? 'portal_usage.chart_read_one' : 'portal_usage.chart_read_many';
    const fallback = value === 1
        ? 'Semana del {{week}} · {{n}} consulta del cliente'
        : 'Semana del {{week}} · {{n}} consultas del cliente';
    return t(key, fallback, {week, n: value, interpolation: {escapeValue: false}});
}

export default PortalWeeklyChart;
